import React from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { SpotlightTarget } from '@atlaskit/onboarding';
import FileIcon from '@atlaskit/icon/core/file';
import HomeIcon from '@atlaskit/icon/core/home';
import ClockIcon from '@atlaskit/icon/core/clock';
import StarUnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import TaskIcon from '@atlaskit/icon/core/task';

import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';

import { View } from '../homeRouteParams.ts';
import { TaskNavigationItem } from '../HomeViews/Tasks/TaskNavigationItem.tsx';
import { NAV_COLLAPSE_BREAKPOINT_PX } from '../layoutConstants.ts';
import { LazyLeftPanelExtendedItems } from '../LeftPanel/index.tsx';

import { HomeNavigationItem } from './HomeNavigationItem.tsx';

const i18n = defineMessages({
	overview: {
		id: 'home.navigation.overview',
		defaultMessage: 'ForYou',
		description: 'Link to an overview of the users work',
	},
	recent: {
		id: 'home.navigation.recent',
		defaultMessage: 'Recent',
		description: 'Link to a list of the users recent work',
	},
	starred: {
		id: 'home.navigation.starred',
		defaultMessage: 'Starred',
		description: 'Link to a list of pages the user starred',
	},
	watching: {
		id: 'home.navigation.watching',
		defaultMessage: 'Watching',
		description: 'Link to a list of pages the user is watching',
	},
	drafts: {
		id: 'home.navigation.drafts',
		defaultMessage: 'Drafts',
		description: 'Link to a list of the users drafts',
	},
	goToPersonalSpace: {
		id: 'home.navigation.go-personal-space',
		defaultMessage: 'Your space',
		description:
			"Text of a button which links to the user's personal space. Displayed underneath avatar and username as part of profile card.",
	},
	tasks: {
		id: 'home.navigation.tasks-home',
		defaultMessage: 'Tasks',
		description: 'Link to a list of tasks assigned to the user or created by them',
	},
	side: {
		id: 'home.navigation.side',
		defaultMessage: 'Side',
		description: 'Aria label for the side navigation',
	},
});

const navigationLinksSectionStyles = css({
	width: '100%',
});

const cardStyles = css({
	alignItems: 'center',
	// Enforce width only if nav isn't collapsed
	maxWidth: '240px',
	position: 'relative',
	minWidth: 'calc(240px - 240px * var(--homeNavIsCollapsed))',
	'--homeIsLegacy': 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'a:selected svg': {
		fill: token('color.link'),
	},
	// No shadow or background if nav is collapsed
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
	[`@media (max-width: ${NAV_COLLAPSE_BREAKPOINT_PX}px)`]: {
		backgroundColor: 'transparent',
		boxShadow: 'none',
	},
});

const spotlightContainerStyles = css({
	position: 'absolute',
	top: token('space.negative.050'),
	left: token('space.negative.050'),
	padding: token('space.050'),
	height: 'calc(100% - 8px)',
	width: '100%',
	pointerEvents: 'none',
});

const linksSectionListStyles = css({
	listStyle: 'none',
	padding: 0,
	margin: 0,
});

const linksSectionListItemStyles = css({
	margin: 0,
	padding: 0,
});

export const HomeNavigation = () => {
	const { formatMessage } = useIntl();

	return (
		<div css={cardStyles} data-testid="HomeNavigation-card">
			<SuspenseWithDataFetcher dataFetcherName="side-navigation">
				<SpotlightTarget name="home-navigation">
					<div css={spotlightContainerStyles} />
				</SpotlightTarget>
				<nav css={navigationLinksSectionStyles} aria-label={formatMessage(i18n.side)}>
					<ul css={linksSectionListStyles}>
						<li css={linksSectionListItemStyles}>
							<Tooltip content={<FormattedMessage {...i18n.overview} />}>
								<HomeNavigationItem
									view={View.OVERVIEW}
									icon={<HomeIcon label="" spacing="spacious" />}
								>
									<FormattedMessage {...i18n.overview} />
								</HomeNavigationItem>
							</Tooltip>
						</li>
						<li css={linksSectionListItemStyles}>
							<Tooltip content={<FormattedMessage {...i18n.recent} />}>
								<HomeNavigationItem
									view={View.RECENT}
									icon={<ClockIcon label="" spacing="spacious" />}
								>
									<FormattedMessage {...i18n.recent} />
								</HomeNavigationItem>
							</Tooltip>
						</li>
						<li css={linksSectionListItemStyles}>
							<Tooltip content={<FormattedMessage {...i18n.starred} />}>
								<HomeNavigationItem
									view={View.STARRED}
									icon={<StarUnstarredIcon label="" spacing="spacious" />}
								>
									<FormattedMessage {...i18n.starred} />
								</HomeNavigationItem>{' '}
							</Tooltip>
						</li>
						<li css={linksSectionListItemStyles}>
							<Tooltip content={<FormattedMessage {...i18n.drafts} />}>
								<HomeNavigationItem
									view={View.DRAFTS}
									icon={<FileIcon label="" spacing="spacious" />}
								>
									<FormattedMessage {...i18n.drafts} />
								</HomeNavigationItem>{' '}
							</Tooltip>
						</li>
						<li css={linksSectionListItemStyles}>
							<Tooltip content={<FormattedMessage {...i18n.tasks} />}>
								<HomeNavigationItem
									view={View.TASKS}
									icon={<TaskIcon label="" spacing="spacious" />}
									render={(props) => <TaskNavigationItem {...props} />}
								/>{' '}
							</Tooltip>
						</li>
					</ul>
				</nav>
				<LazyLeftPanelExtendedItems />
			</SuspenseWithDataFetcher>
		</div>
	);
};
